import Secure from "@/secure/payload";
import { API_URLS } from "@/utils/constant/api.constant";
import { FORMATE } from "@/utils/constant/global.constant";
import { C_API } from "@/utils/helper/api";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import moment from "moment";
import { getUploadUrls } from "@/services/common/docs.services";

// export const fetchEmployeeDetails = async (employeeId: string) => {
//   const response = await axios.get(`${process.env.NEXT_PUBLIC_BASE_API_URL}/employees/${employeeId}`);
//   return response.data;
// };

// export const useEmployeeDetails = (employeeId: string) => {
//   return useQuery({
//     queryKey: ["employee", employeeId],
//     queryFn: () => fetchEmployeeDetails(employeeId),
//     enabled: !!employeeId,
//     staleTime: 5 * 60 * 1000,
//   });
// };

export const fetchOrgEmployees = async (payload: any) => {
  const response = await C_API.post(`${API_URLS.EMPLOYEE.GET_ORGS_EMP}`, payload);
  return response.data?.data;
};

export const getEmployeeShareLink = async (employeeId: string) => {
  const response = await axios.get(`${API_URLS.EMPLOYEE.GET_ORGS_EMP}/${employeeId}`);
  return response.data;
};

export const useOrgEmployee = (currentOrg: any, payload: any) => {
  const { enabled = false, ...rest } = payload || {};
  return useQuery({
    queryKey: ["employee", rest, `ORG${currentOrg.org_id}`],
    queryFn: () => fetchOrgEmployees(rest),
    staleTime: 5 * 60 * 1000,
    enabled,
    retry: false,
  });
};

export const addEmployee = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.ADD_EMP, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const deleteEmployee = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.DELETE_EMP, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const deleteAttachment = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.ACTION_ATTACHMENT, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const deleteNote = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.NOTE_ACTION, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const updateNote = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["note"] });
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.NOTE_ACTION, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const deleteSignatory = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.DELETE_SIG, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const updateEmployee = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["token"] });
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.UPDATE_EMP, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const checkoutEmployeeReport = async (data: any): Promise<any> => {
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE_REPORT.CHECKOUT, data);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const terminateEmployee = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["token"] });
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.EXIT_EMP, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

interface DocInfoRes {
  price: string;
  currency: string;
}

const fetchShareDocInfo = async (params: { share_id?: string }): Promise<DocInfoRes> => {
  const response = await C_API.get(API_URLS.EMPLOYEE_REPORT.DOC_INFO, { params: params });
  return response.data?.data;
};

export const useShareDocInfo = (params: { share_id?: string; hostedPageId?: string | null }) => {
  return useQuery<DocInfoRes, Error>({
    queryKey: ["shareDoc"],
    queryFn: () => fetchShareDocInfo(params),
    refetchInterval: 0,
    enabled: !!params.share_id && !params.hostedPageId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
};
interface PaymentValidateRes {
  status: string;
  download_link: string;
}

const fetchSharePaymentValidate = async (params: { share_id?: string; hostedpage_id?: string }): Promise<PaymentValidateRes> => {
  const response = await C_API.get(API_URLS.EMPLOYEE_REPORT.PAYMENT_VALIDATION, { params: params });
  return response.data?.data;
};
const globalQuery = async (params: { q?: string }): Promise<PaymentValidateRes> => {
  const encodedParams = {
    ...params,
    q: params.q ? encodeURIComponent(params.q) : params.q,
  };
  const response = await C_API.get(API_URLS.EMPLOYEE.GLOBAL_QUERY, { params: encodedParams });
  return response.data?.data;
};
export const useGlobalSearch = (params: { q?: string }) => {
  return useQuery<PaymentValidateRes, Error>({
    queryKey: ["paymentVal"],
    queryFn: () => globalQuery(params),
    refetchInterval: 0,
    enabled: !!params.q,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

export const useSharePaymentValidate = (params: { share_id?: string; hostedpage_id?: string }) => {
  return useQuery<PaymentValidateRes, Error>({
    queryKey: ["paymentVal"],
    queryFn: () => fetchSharePaymentValidate(params),
    refetchInterval: 0,
    enabled: !!params.share_id,
    staleTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
};

interface EmployeeShareLinkRes {
  shareLink: string;
  docLink: string;
}

export const fetchEmployeeShareLink = async (data: any): Promise<EmployeeShareLinkRes> => {
  const encryptData = Secure.payloadEncryption(data, { excludedKeys: ["fields", "AdditionalInfo", "docPlanId"] });
  const response = await C_API.post(API_URLS.EMPLOYEE.SHARE_EMP, encryptData);
  return response.data?.data;
};
export const reinviteEmployee = async (data: any) => {
  const encryptData = Secure.payloadEncryption(data);
  const response = await C_API.post(API_URLS.EMPLOYEE.REINVITE, encryptData);
  return { status: response.status, data: response.data };
};

export const addEmployeeAttachment = async (dataObj: any) => {
  const encryptData = Secure.payloadEncryption(dataObj, { excludedKeys: ["docs"] });
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.ADD_ATTACHMENT, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const addEmployeeNote = async (dataObj: any) => {
  const encryptData = Secure.payloadEncryption(dataObj, { excludedKeys: ["note"] });
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.ADD_NOTE, encryptData);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const addConfig = async (key: string, dataObj: any) => {
  try {
    const response = await C_API.post(`${API_URLS.EMPLOYEE.CONFIG}?configKey=${key}`, dataObj);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};
export const getOrgConfig = async (key: string) => {
  try {
    const response = await C_API.get(`${API_URLS.EMPLOYEE.CONFIG}?configKey=${key}`);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

export const fetchEmployeeInfo = async (params: { empId?: string; view_typ: string; query?: string }): Promise<any> => {
  const response = await C_API.get(API_URLS.EMPLOYEE.INFO, { params: params });
  return response.data?.data;
};

export const useEmployeeInfo = (params: { empId?: string; view_typ: string; query?: string; type?: string; filter?: any }) => {
  let fromDate, toDate;
  const filterTimeVal = params.filter?.time?.value;

  if (filterTimeVal === "customPeriod") {
    fromDate = params.filter?.time?.fromDate;
    toDate = params.filter?.time?.toDate;
  } else if (filterTimeVal === "today") {
    fromDate = moment().format(FORMATE.DATE);
  } else if (filterTimeVal === "yesterday") {
    const yesterday = moment().subtract(1, "day").format(FORMATE.DATE);
    fromDate = yesterday;
  } else if (filterTimeVal === "last7days") {
    fromDate = moment().subtract(6, "days").format(FORMATE.DATE);
    toDate = moment().format(FORMATE.DATE);
  } else if (filterTimeVal === "last30days") {
    fromDate = moment().subtract(29, "days").format(FORMATE.DATE);
    toDate = moment().format(FORMATE.DATE);
  }

  const cleanParams = {
    ...params,
    // Pass the raw filter data to backend
    filterOperator: params.filter?.time?.value,
    filterFromDate: params.filter?.time?.fromDate, // Only for custom period
    filterToDate: params.filter?.time?.toDate, // Only for custom period'
    fromDate,
    toDate,
  };

  // Remove undefined values
  const finalParams = Object.fromEntries(Object.entries(cleanParams).filter(([_, v]) => v !== undefined)) as {
    empId?: string;
    view_typ: string;
    query?: string;
    type?: string;
    filterOperator?: string;
    filterFromDate?: string;
    filterToDate?: string;
  };

  return useQuery<any, Error>({
    queryKey: ["employeeInfo", ...Object.values(finalParams)],
    queryFn: () => fetchEmployeeInfo(finalParams),
    refetchInterval: 0,
    enabled: !!params.empId,
    staleTime: 0,
    refetchOnWindowFocus: false,
    retry: false,
  });
};

export const importEmployees = async (data: any, onUploadProgress?: (progress: number) => void) => {
  try {
    // First upload the CSV file
    const response: any = await getUploadUrls({
      fileName: data.file.name,
      fileType: data.file.type,
      uploadKey: "employee-import",
    });

    const { preSignedUrl, objectKey } = response.data?.data || {};

    // Upload file to S3
    await axios.put(preSignedUrl, data.file, {
      headers: {
        "Content-Type": data.file.type,
      },
      onUploadProgress: (progressEvent) => {
        if (onUploadProgress) {
          const percentCompleted = Math.round(((progressEvent.loaded || 0) * 100) / (progressEvent.total || 1));
          onUploadProgress(percentCompleted);
        }
      },
    });

    // Prepare field mapping object
    const fieldMapping: any = {};
    data.fieldMappings.forEach((mapping: any) => {
      if (mapping.mapped && mapping.lystlocField) {
        fieldMapping[mapping.fileColumn] = mapping.lystlocField;
      }
    });

    // Prepare import payload
    const importPayload = {
      mode: data.importAction === "new" ? "create" : data.importAction === "update" ? "update" : "both",
      csv_url_key: objectKey,
      field_mapping: fieldMapping,
      ...(data.keyField && { keyField: data.keyField }),
    };
    const importResponse = await C_API.post(API_URLS.EMPLOYEE.IMPORT, importPayload);
    return importResponse.data;
  } catch (error) {
    console.error("Error importing employees:", error);
    throw error;
  }
};

// Fetch import history
const fetchImportHistory = async (params: any) => {
  const response = await C_API.get(API_URLS.EMPLOYEE.IMPORT_HISTORY, { params });
  return response.data?.data;
};

export const useImportHistory = (params: any) => {
  return useQuery({
    queryKey: ["importHistory", params],
    queryFn: () => fetchImportHistory(params),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
};

const fetchImportDetails = async (importId: string, page?: number, tab?: string, perPage?: number) => {
  const params = { page, tab, per_page: perPage };
  const res = await C_API.get(`/employee/import-details/${importId}`, { params });
  return res.data?.data;
};
export const useImportDetails = (importId: string, page?: number, tab?: string, perPage?: number) => {
  return useQuery({
    queryKey: ["importDetails", importId, page, tab, perPage],
    queryFn: () => fetchImportDetails(importId, page, tab, perPage),
    enabled: !!importId,
    staleTime: 30 * 1000,
    retry: false,
  });
};

export const webaccess = async (data: any) => {
  try {
    const response = await C_API.post(API_URLS.EMPLOYEE.WEB_ACCESS, data);
    return { status: response.status, data: response.data };
  } catch (err) {
    return err;
  }
};

interface Plan {
  id: number;
  price: number;
  currency: string;
}

export const fetchEmployeePlans = async (): Promise<Plan[]> => {
  try {
    const response = await C_API.get(API_URLS.EMPLOYEE.PLANS);
    // Handle both response formats: response.data.data or response.data directly
    return response.data?.data || response.data || [];
  } catch (error) {
    console.error('Error fetching employee plans:', error);
    return [];
  }
};

export const useEmployeePlans = (enabled: boolean = false) => {
  return useQuery<Plan[], Error>({
    queryKey: ["employeePlans"],
    queryFn: () => fetchEmployeePlans(),
    refetchInterval: 0,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: false,
    enabled, // Only fetch when enabled (dropdown is open)
  });
};